import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, Building2, CreditCard, Database, Loader2 } from 'lucide-react';
import {
  REPORT_VERTICALS, KPI_META, kpisPara, btypeLabel, normalizarVertical, activity30dPara,
} from './reportCatalog';
import { PLAN_LABELS, PLAN_COLORS } from './constants';

function Kpi({ label, value, color }) {
  const tones = {
    orange: 'bg-orange-500/10 text-orange-400 border-orange-500/10',
    sky: 'bg-sky-500/10 text-sky-400 border-sky-500/10',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/10',
    violet: 'bg-violet-500/10 text-violet-400 border-violet-500/10',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/10',
    cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/10',
    green: 'bg-green-500/10 text-green-400 border-green-500/10',
  };
  const cls = tones[color] || tones.violet;
  return (
    <Card className={`border ${cls} backdrop-blur-md`}>
      <CardContent className="p-4">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="text-lg font-bold text-white">{value}</p>
      </CardContent>
    </Card>
  );
}

export default function ReportsPanel() {
  const [vertical, setVertical] = useState('todas');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [catalogs, setCatalogs] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [biBuilder, setBiBuilder] = useState({
    dataset_key: 'leads', dimensions: ['source'],
    metrics: [{ op: 'count', field: 'id', label: 'Conteo' }],
    filters: {}, limit: 20, chart_type: 'bar',
  });
  const [biResult, setBiResult] = useState(null);
  const [biLoading, setBiLoading] = useState(false);

  const load = async (v) => {
    const vert = normalizarVertical(v);
    setLoading(true);
    setCatalogLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get(`/admin/reports/advanced?vertical=${vert}`),
        api.get(`/admin/data-catalogs?vertical=${vert}`),
      ]);
      setReport(r.data);
      const cats = c.data || [];
      setCatalogs(cats);
      if (cats.length && !cats.some((x) => x.key === biBuilder.dataset_key)) {
        setBiBuilder((prev) => ({
          ...prev,
          dataset_key: cats[0].key,
          dimensions: [cats[0].fields?.[0] || 'id'],
        }));
      }
    } catch {
      setReport(null);
      setCatalogs([]);
    } finally {
      setLoading(false);
      setCatalogLoading(false);
    }
  };

  useEffect(() => { load(vertical); }, [vertical]); // eslint-disable-line

  const runBi = async () => {
    setBiLoading(true);
    try {
      const r = await api.post('/admin/bi/query', { ...biBuilder, vertical });
      setBiResult(r.data);
    } catch {
      toast.error('No se pudo generar el informe');
    } finally {
      setBiLoading(false);
    }
  };

  const exportar = (key, format) => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${base}/api/admin/export-dataset/${key}?format=${format}&vertical=${vertical}`, '_blank');
  };

  const usage = report?.usage || {};
  const kpis = kpisPara(vertical);

  return (
    <div className="space-y-6" data-testid="advanced-reports">
      <div className="inline-flex gap-1 p-1 rounded-lg bg-black/30 border border-white/10" data-testid="reports-verticals">
        {REPORT_VERTICALS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => { setVertical(t.key); setBiResult(null); }}
            className={`px-3 py-1.5 rounded-md text-[11px] font-medium ${vertical === t.key ? 'bg-violet-500/20 text-violet-200' : 'text-white/50 hover:text-white'}`}
            data-testid={`reports-vertical-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground -mt-4">
        Solo Super Admin. Autos no mezcla propiedades; desarrollos no mezcla casas. El CRM del tenant no se toca.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-violet-400 animate-spin" /></div>
      ) : report ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {kpis.map((key) => {
              const meta = KPI_META[key] || { label: key, color: 'violet' };
              const val = usage[key] ?? report.companies?.[key];
              const n = Number(val || 0);
              return <Kpi key={key} label={meta.label} value={n.toLocaleString()} color={meta.color} />;
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-card/50 backdrop-blur-md border-white/5">
              <CardContent className="p-5">
                <h4 className="text-sm font-heading font-semibold text-white mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-violet-400" /> Empresas vivas
                </h4>
                <div className="flex items-center gap-4">
                  <div className="text-center p-3 rounded-lg bg-violet-500/5 border border-violet-500/10 flex-1">
                    <p className="text-xl font-bold text-violet-400">{report.companies?.total || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Total</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-violet-500/5 border border-violet-500/10 flex-1">
                    <p className="text-xl font-bold text-violet-400">+{report.companies?.new_30d || 0}</p>
                    <p className="text-[10px] text-muted-foreground">30 días</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card/50 backdrop-blur-md border-white/5">
              <CardContent className="p-5">
                <h4 className="text-sm font-heading font-semibold text-white mb-3">Por tipo</h4>
                <div className="space-y-2">
                  {Object.entries(report.companies?.by_business_type || {}).map(([bt, count]) => (
                    <div key={bt} className="flex items-center justify-between p-2.5 rounded-lg bg-black/20 border border-white/5">
                      <span className="text-xs text-white">{btypeLabel(bt)}</span>
                      <Badge className="text-[10px] bg-white/5">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {report.monthly_signups?.length > 0 && (
            <Card className="bg-card/50 backdrop-blur-md border-white/5">
              <CardContent className="p-5">
                <h4 className="text-sm font-heading font-semibold text-white mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-cyan-400" /> Registros mensuales
                </h4>
                <div className="flex items-end gap-3 h-32">
                  {report.monthly_signups.map((m, i) => {
                    const maxVal = Math.max(...report.monthly_signups.map((x) => x.count), 1);
                    return (
                      <div key={m.label || i} className="flex-1 flex flex-col items-center gap-1">
                        <div
                          className="w-full rounded-t bg-cyan-500/30 hover:bg-cyan-500/50 transition-colors relative group"
                          style={{ height: `${Math.max((m.count / maxVal) * 100, 4)}%` }}
                        >
                          <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-[#0d1117] border border-white/10 rounded px-1.5 py-0.5 text-[8px] text-white opacity-0 group-hover:opacity-100 transition-opacity">{m.count}</div>
                        </div>
                        <span className="text-[8px] text-muted-foreground">{m.label}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="bg-card/50 backdrop-blur-md border-white/5">
            <CardContent className="p-5">
              <h4 className="text-sm font-heading font-semibold text-white mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-emerald-400" /> Actividad últimos 30 días
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {activity30dPara(vertical).map((item) => {
                  const n = Number(usage[item.key] || 0);
                  const tone = item.color === 'amber' ? 'text-amber-400 bg-amber-500/5 border-amber-500/10'
                    : item.color === 'green' ? 'text-green-400 bg-green-500/5 border-green-500/10'
                    : 'text-emerald-400 bg-emerald-500/5 border-emerald-500/10';
                  return (
                    <div key={item.key} className={`p-3 rounded-lg border text-center ${tone}`}>
                      <p className="text-lg font-bold">+{n.toLocaleString()}</p>
                      <p className="text-[10px] text-muted-foreground">{item.label}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {report.top_companies?.length > 0 && (
            <Card className="bg-card/50 backdrop-blur-md border-white/5">
              <CardContent className="p-5">
                <h4 className="text-sm font-heading font-semibold text-white mb-3">Top por leads</h4>
                <div className="space-y-2">
                  {report.top_companies.map((tc, i) => (
                    <div key={tc.company_id || i} className="flex items-center gap-3 p-3 rounded-lg bg-black/20 border border-white/5">
                      <div className="w-7 h-7 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-400 text-[10px] font-bold">#{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-white truncate">{tc.name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Badge className="text-[8px] bg-white/5">{btypeLabel(tc.business_type)}</Badge>
                          <Badge className={`text-[8px] ${PLAN_COLORS[tc.plan] || 'bg-white/5'}`}>{PLAN_LABELS[tc.plan] || tc.plan}</Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-white">{tc.count}</p>
                        <p className="text-[9px] text-muted-foreground">leads</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid xl:grid-cols-[0.9fr_1.1fr] gap-6">
            <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="data-exporter-card">
              <CardContent className="p-5 space-y-4">
                <div>
                  <h4 className="text-sm font-heading font-semibold text-white mb-1 flex items-center gap-2">
                    <Database className="w-4 h-4 text-cyan-400" /> Datasets de esta vertical
                  </h4>
                  <p className="text-[10px] text-muted-foreground">Campos fijos, sin secretos. No es un dump de Mongo.</p>
                </div>
                {catalogLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-cyan-300" /></div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                    {catalogs.map((cat) => (
                      <div key={cat.key} className="p-3 rounded-lg bg-black/20 border border-white/5">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-xs text-white font-medium">{cat.label}</p>
                            <p className="text-[10px] text-muted-foreground">{cat.count} registros · {cat.fields.length} campos</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => exportar(cat.key, 'csv')} className="text-[10px] h-7 border-white/10">CSV</Button>
                            <Button size="sm" variant="outline" onClick={() => exportar(cat.key, 'json')} className="text-[10px] h-7 border-white/10">JSON</Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="bi-studio-card">
              <CardContent className="p-5 space-y-4">
                <div>
                  <h4 className="text-sm font-heading font-semibold text-white mb-1 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-emerald-400" /> BI Studio
                  </h4>
                  <p className="text-[10px] text-muted-foreground">Sobre el catálogo de la vertical. El builder plano viejo queda detrás: este responde 200.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <Select value={biBuilder.dataset_key} onValueChange={(v) => {
                    const catalog = catalogs.find((c) => c.key === v);
                    setBiBuilder({ ...biBuilder, dataset_key: v, dimensions: [catalog?.fields?.[0] || 'id'], metrics: [{ op: 'count', field: 'id', label: 'Conteo' }] });
                  }}>
                    <SelectTrigger className="bg-black/20 border-white/10 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{catalogs.map((cat) => <SelectItem key={cat.key} value={cat.key}>{cat.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={biBuilder.chart_type} onValueChange={(v) => setBiBuilder({ ...biBuilder, chart_type: v })}>
                    <SelectTrigger className="bg-black/20 border-white/10 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bar">Barras</SelectItem>
                      <SelectItem value="line">Línea</SelectItem>
                      <SelectItem value="pie">Pastel</SelectItem>
                      <SelectItem value="table">Tabla</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={biBuilder.dimensions[0] || 'id'} onValueChange={(v) => setBiBuilder({ ...biBuilder, dimensions: [v] })}>
                    <SelectTrigger className="bg-black/20 border-white/10 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{(catalogs.find((c) => c.key === biBuilder.dataset_key)?.fields || []).map((field) => <SelectItem key={field} value={field}>{field}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Select value={biBuilder.metrics[0]?.op || 'count'} onValueChange={(v) => setBiBuilder({ ...biBuilder, metrics: [{ ...biBuilder.metrics[0], op: v }] })}>
                      <SelectTrigger className="bg-black/20 border-white/10 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="count">Conteo</SelectItem>
                        <SelectItem value="sum">Suma</SelectItem>
                        <SelectItem value="avg">Promedio</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={biBuilder.metrics[0]?.field || 'id'} onValueChange={(v) => setBiBuilder({ ...biBuilder, metrics: [{ ...biBuilder.metrics[0], field: v }] })}>
                      <SelectTrigger className="bg-black/20 border-white/10 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{(catalogs.find((c) => c.key === biBuilder.dataset_key)?.fields || []).map((field) => <SelectItem key={field} value={field}>{field}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={runBi} className="bg-emerald-600 hover:bg-emerald-500 text-xs">Generar informe</Button>
                {biLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-emerald-300" /></div>
                ) : biResult?.rows?.length > 0 && (
                  <div className="overflow-auto border border-white/5 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-black/20">
                        <tr>{Object.keys(biResult.rows[0]).map((col) => <th key={col} className="text-left px-3 py-2 text-muted-foreground">{col}</th>)}</tr>
                      </thead>
                      <tbody>
                        {biResult.rows.map((row, idx) => (
                          <tr key={idx} className="border-t border-white/5">
                            {Object.values(row).map((val, j) => <td key={j} className="px-3 py-2 text-white/70">{String(val)}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        <div className="text-center py-20">
          <BarChart3 className="w-12 h-12 text-muted-foreground/20 mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">No se pudieron cargar los reportes</p>
        </div>
      )}
    </div>
  );
}
